import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useIsExternalUser } from '~/hooks/useIsExternalUser';
import { isEnvEnabled } from '~/lib/env';
import { useLabelGroupList } from '~/modules/settings/api/queries/labelGroupList';
import { useLabelList } from '~/modules/settings/api/queries/labelList';
import {
  EXTERNAL_ROLES,
  INTERNAL_ROLES,
  LABEL_TYPES,
  USER_STATUS,
  USER_TYPES,
} from '~/modules/settings/constants/Filter';
import { DEFAULT_DROPDOWN_PAGE_SIZE } from '~settings/constants';

import { idToName } from './idToName';

const DEFAULT_PARAMS = {
  skip: 0,
  take: DEFAULT_DROPDOWN_PAGE_SIZE,
};

const mapOptions = (
  options: { value: string; label: string }[],
  t: (key: string) => string,
) =>
  options.map((option) => ({
    id: option.value,
    name: t(option.label),
  }));

export interface FilterData {
  id: string;
  name: string;
}

export interface FilterChips {
  title: { id: string; label: string };
  value: FilterData[];
}

export type FilterEntity = 'users' | 'labels';

export const useFilterChips = (entity: FilterEntity = 'users') => {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation('settings');

  const labelGroupsEnabled = isEnvEnabled(
    'VITE_CONTAINER_SETTINGS_LABEL_GROUPS_ENABLED',
    false,
  );
  const isCurrentUserInternal = !useIsExternalUser();

  const shouldLoadLabelGroups =
    labelGroupsEnabled &&
    isCurrentUserInternal &&
    searchParams.has('labelGroupIds');

  const { labelList } = useLabelList(DEFAULT_PARAMS, {
    select: (data) =>
      data.data.map((label) => ({ id: label.id, name: label.name })),
  });
  const { labelGroupList } = useLabelGroupList(DEFAULT_PARAMS, {
    select: (data) =>
      data.data.map((label) => ({ id: label.id, name: label.name })),
    enabled: shouldLoadLabelGroups,
  });
  const { labelList: parentList } = useLabelList(
    {
      ...DEFAULT_PARAMS,
      labelType: 'parent_label',
    },
    {
      select: (data) =>
        data.data.map((label) => ({ id: label.id, name: label.name })),
    },
  );

  const ROLE_OPTIONS = mapOptions(INTERNAL_ROLES.concat(EXTERNAL_ROLES), t);
  const USER_OPTIONS = mapOptions(USER_TYPES, t);
  const USER_ACTIVITY = mapOptions(USER_STATUS, t);
  const LABEL_OPTIONS = mapOptions(LABEL_TYPES, t);

  const filters: FilterChips[] = [];

  const filterOptions: Record<
    FilterEntity,
    {
      key: string;
      title: string;
      options: FilterData[] | undefined;
    }[]
  > = {
    users: [
      { key: 'isActive', title: 'Status', options: USER_ACTIVITY },
      { key: 'roles', title: 'Role', options: ROLE_OPTIONS },
      { key: 'isInternal', title: 'User Type', options: USER_OPTIONS },
      { key: 'labelIds', title: 'Label', options: labelList },
      { key: 'labelGroupIds', title: 'Label Group', options: labelGroupList },
    ],
    labels: [
      { key: 'roles', title: 'Role', options: ROLE_OPTIONS },
      { key: 'labelType', title: 'Label Type', options: LABEL_OPTIONS },
      { key: 'labelGroupIds', title: 'Label Group', options: labelGroupList },
      { key: 'parentIds', title: 'Parent Label', options: parentList },
    ],
  };

  if (filterOptions[entity]) {
    for (const { key, title, options } of filterOptions[entity]) {
      const ids = searchParams.get(key)?.split(',').filter(Boolean);

      if (!ids) {
        continue;
      }

      filters.push({
        title: { id: key, label: title },
        value: options ? idToName({ ids, data: options }) : [],
      });
    }
  }

  return filters;
};
